import React, { HTMLProps, PropsWithChildren } from 'react';
import { RouteConfigComponentProps } from 'react-router-config';
import { Schema } from '@cp/ds/src/components/schema';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { Typography } from '@cp/ds/src/components/typography';
import { Container } from '@cp/ds/src/components/container';
import { styled } from '@cp/ds/src/theme/styled';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds';
import { useIsMobile } from '@cp/ds/src/hooks';
import { useIsEmbedded } from 'src/shared/mediaQueries';
import { UXSlice, loginShownSelector } from 'src/shared/ux';
import { useAuth } from 'src/features/auth';
import { ShowcaseGrid } from 'src/widgets/showcase';
import { Feedbacks } from 'src/widgets/catalog';
import { HOME_FEEDBACKS_LIST_PARAMETERS } from 'src/app/routes/home/const';
import { Media } from 'src/app/shared/components/media';
import vectorMobile from 'src/assets/shapes/vectors/vectorFullWidthSmall.svg?url';
import vectorMobileInverse from 'src/assets/shapes/vectors/vectorFullWidthSmallInverse.svg?url';
import vectorDesktop from 'src/assets/shapes/vectors/vectorFullWidth.svg?url';
import vectorDesktopInverse from 'src/assets/shapes/vectors/vectorFullWidthInverse.svg?url';
import { cssToken } from '@youhelp-ru/ui';
import { useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { useCurrentCity } from '@cp/entities/location';
import { CitySelection } from 'src/widgets/location';
import { getEmploymentAgencySchema } from '../../shared/schemas';
import { usePageMetaTags } from '../../shared/hooks/useMetaTags';
import { PageMetaTags } from '../../shared/components/layout/components/meta';
import { FaqBlock } from '../home/components/faq';
import { TrustedSpecialists } from './components/trustedSpecialists';
import { HomeBanners } from './components/bannersNew';
import { HowItWork } from './components/howItWork';
import { HomeIntro } from './components/introNew';
import { LazyFeedbacksSlider } from './components/feedbacksSlider/lazyFeedbacksSlider';
const name = 'section';
const SectionTokens = {
  Background: cssToken(name, 'background'),
};
const SectionRoot = styled('section', { name })(({ theme }) => ({
  '&.has-adornment:before': {
    backgroundColor: SectionTokens.Background(),
    content: '""',
    display: 'block',
    maskImage: `url(${vectorMobile})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center top',
    maskSize: 'cover',
    maskMode: 'luminance',
    aspectRatio: '375/40',
    width: '100%',
    transform: 'scaleX(-1)',
    transformOrigin: 'center',
  },
  '&.bottom-adornment:after': {
    backgroundColor: SectionTokens.Background(),
    content: '""',
    display: 'block',
    maskImage: `url(${vectorMobileInverse})`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center top',
    maskSize: 'cover',
    maskMode: 'luminance',
    aspectRatio: '375/40',
    width: '100%',
  },
  [theme.breakpoints.up(MOBILE_BREAKPOINT_THRESHOLD)]: {
    '&.has-adornment:before': {
      aspectRatio: '1920/114',
      maskImage: `url(${vectorDesktop})`,
    },
    '&.bottom-adornment:after': {
      aspectRatio: '1920/114',
      maskImage: `url(${vectorDesktopInverse})`,
    },
  },
}));
const SectionWrapper = styled('div', { name, slot: 'wrapper' })({
  backgroundColor: SectionTokens.Background(),
  paddingBlock: '1px',
});
export interface SectionProps extends PropsWithChildren, Omit<HTMLProps<HTMLDivElement>, 'as'> {
  sx?: SxProps;
}
const Section = ({ children, ...props }: SectionProps) => {
  return (
    <SectionRoot {...props}>
      <SectionWrapper>{children}</SectionWrapper>
    </SectionRoot>
  );
};

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: '28px',
  marginBottom: '20px',
  [theme.breakpoints.up('md')]: {
    marginTop: '60px',
    marginBottom: '48px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2.5rem',
  },
}));

const HomePage: React.FC<RouteConfigComponentProps> = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const metaTagsRenderConfig = usePageMetaTags();
  const { hasCitySeparatedContent } = useClientWebRouteParams();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });

  const employmentAgencySchema = React.useMemo(() => getEmploymentAgencySchema(), []);

  const isEmbedded = useIsEmbedded();
  const isLoginShown = useSelector(loginShownSelector);
  const login = useAuth();
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (isEmbedded && !isLoginShown) {
      login();
      dispatch(UXSlice.actions.setloginShown(true));
    }
  }, [dispatch, isLoginShown, isEmbedded, login]);

  const baseColor = theme.palette['gray ultra light'];
  const feedbacksParams = React.useMemo(() => {
    if (hasCitySeparatedContent && currentCity) {
      return { ...HOME_FEEDBACKS_LIST_PARAMETERS, cityId: currentCity.id };
    }
    return HOME_FEEDBACKS_LIST_PARAMETERS;
  }, [currentCity, hasCitySeparatedContent]);

  return (
    <main>
      <PageMetaTags renderConfig={metaTagsRenderConfig} />

      <Section className="bottom-adornment" sx={{ [SectionTokens.Background]: baseColor }}>
        <HomeIntro />
      </Section>

      <Section>
        <Container maxWidth={1430} sx={{ containerType: 'inline-size' }}>
          <StyledSectionTitle component="h2" variant="h2">
            Как это работает
          </StyledSectionTitle>
          <HowItWork />
        </Container>
      </Section>

      <Section className="has-adornment bottom-adornment" sx={{ [SectionTokens.Background]: baseColor }}>
        <HomeBanners />
        <Container maxWidth={1430} sx={{ containerType: 'inline-size' }}>
          <StyledSectionTitle component="h2" variant="h2">
            Нашим специалистам {isMobile && <br />}
            можно доверять
          </StyledSectionTitle>
          <TrustedSpecialists />
        </Container>

        <Feedbacks {...feedbacksParams} />
      </Section>

      <Section>
        <Container maxWidth={1430} sx={{ containerType: 'inline-size' }}>
          <StyledSectionTitle component="h2" variant="h2">
            У нас вы можете найти
          </StyledSectionTitle>
          <ShowcaseGrid />
        </Container>
      </Section>

      <Section className="has-adornment bottom-adornment" sx={{ [SectionTokens.Background]: baseColor, width: '100%' }}>
        <Container maxWidth={1430}>
          <StyledSectionTitle component="h2" variant="h2">
            СМИ о нас
          </StyledSectionTitle>
          <Media />
        </Container>

        <StyledSectionTitle component="h2" variant="h2">
          Семьи о наших помощниках
        </StyledSectionTitle>
        <LazyFeedbacksSlider />
      </Section>

      <Section className="has-adornment">
        <Container maxWidth={1430}>
          <StyledSectionTitle component="h2" variant="h2">
            Отвечаем на вопросы
          </StyledSectionTitle>
          <FaqBlock isMobile={isMobile} />
        </Container>
      </Section>

      <Schema schema={employmentAgencySchema} />
      <CitySelection />
    </main>
  );
};

export default HomePage;
